import { createContext, useContext, useState } from 'react';
import { updateDeviceLabel } from '../services/sensors';

type SensorNames = Record<string, string>;

/**
 * Nombres visibles de los sensores.
 *
 * Antes vivían ÚNICAMENTE en localStorage: renombrar desde Suelo contestaba
 * "✓ Saved" y no salía nunca del navegador. El resultado era que el dueño del
 * equipo veía "Santa Maria #1" y todos los demás —otra persona, otra
 * computadora, el panel de administración— seguían viendo el IMEI. Y bastaba
 * limpiar los datos del navegador para perder el nombre.
 *
 * Ahora el nombre se guarda en la base (PATCH /devices/:id, campo `label`).
 * La llave vieja se sigue leyendo, pero sólo para ofrecer subir lo que quedó
 * atrapado ahí; ya no se escribe nada nuevo en ella.
 */
const NAMES_KEY = 'sensor_names';

function cargarHeredados(): SensorNames {
  try { return JSON.parse(localStorage.getItem(NAMES_KEY) ?? '{}'); }
  catch { return {}; }
}

/** Lo mínimo que hace falta de un sensor para renombrarlo. */
export interface DeviceRef {
  id: string;
  imei: string;
  /** Nombre según el SERVIDOR (para un device sin etiqueta, es el IMEI). */
  name: string;
}

interface NamesCtx {
  getName: (imei: string, fallback: string) => string;
  /** Persiste en el servidor. Propaga el error si falla, para que la UI lo diga. */
  rename: (device: DeviceRef, name: string) => Promise<void>;
  /** De esos devices, los que tienen un nombre guardado sólo en este navegador. */
  pendientesDe: (devices: DeviceRef[]) => DeviceRef[];
  subirPendientes: (devices: DeviceRef[]) => Promise<void>;
  descartarPendientes: () => void;
}

const SensorNamesContext = createContext<NamesCtx | null>(null);

export function SensorNamesProvider({ children }: { children: React.ReactNode }) {
  /** Nombres que quedaron sólo en el navegador, de la época en que no se guardaban. */
  const [heredados, setHeredados] = useState<SensorNames>(cargarHeredados);
  /**
   * Renombres de esta sesión. Es una capa optimista: el servidor ya los tiene,
   * pero la lista de sensores puede no haberse refrescado todavía y sin esto
   * el nombre parpadearía de vuelta al viejo justo después de guardarlo.
   */
  const [propios, setPropios] = useState<SensorNames>({});

  const getName = (imei: string, fallback: string) =>
    propios[imei] ?? heredados[imei] ?? fallback;

  const olvidarHeredado = (imei: string) => {
    setHeredados(prev => {
      if (!(imei in prev)) return prev;
      const next = { ...prev };
      delete next[imei];
      if (Object.keys(next).length === 0) localStorage.removeItem(NAMES_KEY);
      else localStorage.setItem(NAMES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const rename = async (device: DeviceRef, name: string) => {
    const limpio = name.trim();
    if (!limpio) return;
    await updateDeviceLabel(device.id, limpio);
    setPropios(prev => ({ ...prev, [device.imei]: limpio }));
    olvidarHeredado(device.imei);
  };

  // Un nombre local que ya coincide con el del servidor no está pendiente de
  // nada: sólo cuentan los que difieren.
  const pendientesDe = (devices: DeviceRef[]) =>
    devices.filter(d => {
      const local = heredados[d.imei];
      return typeof local === 'string' && local !== d.name;
    });

  const subirPendientes = async (devices: DeviceRef[]) => {
    for (const d of pendientesDe(devices)) {
      const local = heredados[d.imei]!;
      await updateDeviceLabel(d.id, local);
      setPropios(prev => ({ ...prev, [d.imei]: local }));
    }
    descartarPendientes();
  };

  const descartarPendientes = () => {
    localStorage.removeItem(NAMES_KEY);
    setHeredados({});
  };

  return (
    <SensorNamesContext.Provider
      value={{ getName, rename, pendientesDe, subirPendientes, descartarPendientes }}
    >
      {children}
    </SensorNamesContext.Provider>
  );
}

export function useSensorNames() {
  const ctx = useContext(SensorNamesContext);
  if (!ctx) throw new Error('useSensorNames must be inside SensorNamesProvider');
  return ctx;
}
